import { PriceRangeType } from 'components/QuoteCard';
import { QuoteRates } from 'lib/api/getQuickQuote';
import { Maybe } from 'types/UtilityTypes';

function isValidRate(value: unknown): value is number {
  return typeof value === 'number' && !Number.isNaN(value);
}

function isPositiveRate(value: unknown): value is number {
  return isValidRate(value) && value > 0;
}

function hasRateBand(low: unknown, high: unknown): boolean {
  return (
    isValidRate(low) &&
    isValidRate(high) &&
    (isPositiveRate(low) || isPositiveRate(high))
  );
}

// Prefer native per-trip target when present; otherwise derive from per-mile.
export function resolveGreenscreensTripCost(
  rates: QuoteRates,
  distance: number
): number {
  if (isPositiveRate(rates.target)) {
    return rates.target;
  }

  if (isPositiveRate(rates.targetPerMile) && distance > 0) {
    return rates.targetPerMile * distance;
  }

  return 0;
}

/**
 * Greenscreens / Triumph quotes often expose buy bands as per-mile only (trip
 * totals may be zero). Builds trip + per-mile price ranges for QuoteCard.
 */
export function buildGreenscreensPriceRanges(
  rates: QuoteRates,
  distance: number
): {
  priceRange: Maybe<PriceRangeType>;
  priceRangePerMile: Maybe<PriceRangeType>;
} {
  const hasPerMileBand = hasRateBand(rates.lowPerMile, rates.highPerMile);
  const hasTripBand = hasRateBand(rates.low, rates.high);

  if (!hasPerMileBand && !hasTripBand) {
    return { priceRange: null, priceRangePerMile: null };
  }

  let low = rates.low;
  let high = rates.high;
  let lowPerMile = rates.lowPerMile;
  let highPerMile = rates.highPerMile;

  if (hasTripBand && !hasPerMileBand && distance > 0) {
    lowPerMile = low / distance;
    highPerMile = high / distance;
  } else if (hasPerMileBand && !hasTripBand && distance > 0) {
    low = lowPerMile * distance;
    high = highPerMile * distance;
  }

  const priceRangePerMile: Maybe<PriceRangeType> =
    hasPerMileBand || (hasTripBand && distance > 0)
      ? { lowEstimate: lowPerMile, highEstimate: highPerMile }
      : null;

  const priceRange: Maybe<PriceRangeType> =
    hasTripBand || (hasPerMileBand && distance > 0)
      ? { lowEstimate: low, highEstimate: high }
      : null;

  return { priceRange, priceRangePerMile };
}
